import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { campaignsApi } from './campaignsApi';
import { getAuthHeaders } from '../../services/http';
import { useSessionStore } from '../../stores/sessionStore';
export type MetaImportFilter = 'all' | 'unimported' | 'active';
export function useMetaCampaignImport(isOpen: boolean, onClose: () => void, onSuccess?: () => void) {
  const token = useSessionStore(state => state.session?.access_token);
  const tenantId = useSessionStore(state => state.activeTenantId);
  const queryClient = useQueryClient();
  const [accountId, setAccountId] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMode, setFilterMode] = useState<MetaImportFilter>('all');
  const headers = getAuthHeaders(token!);
  useEffect(() => { setAccountId(''); setSelectedIds([]); }, [tenantId]);
  const accounts = useQuery({ queryKey: ['meta-accounts', tenantId],
    queryFn: () => campaignsApi.getMetaAccounts({ headers }), enabled: isOpen && Boolean(token) });
  const remote = useQuery({ queryKey: ['meta-remote-campaigns', tenantId, accountId],
    queryFn: () => campaignsApi.listRemoteMetaCampaigns(accountId || undefined, { headers }),
    enabled: isOpen && Boolean(token), staleTime: 60000, retry: false });
  const items = remote.data?.items || [];
  const filtered = items.filter(item => {
    const query = searchTerm.toLocaleLowerCase('pt-BR').trim();
    if (!item.name.toLocaleLowerCase('pt-BR').includes(query) && !item.id.includes(query)) return false;
    return filterMode === 'unimported' ? !item.isImported : filterMode === 'active' ? item.status === 'ACTIVE' : true;
  });
  const mutation = useMutation({
    mutationFn: () => {
      if (!remote.data?.adAccountId || !selectedIds.length || remote.isFetching || remote.isError) {
        throw new Error('Atualize a consulta antes de importar.');
      }
      return campaignsApi.importMetaCampaigns(selectedIds, remote.data.adAccountId, { headers });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['meta-remote-campaigns'] });
      setSelectedIds([]); onSuccess?.(); onClose();
    },
  });
  function changeAccount(value: string) { setAccountId(value); setSelectedIds([]); }
  function toggle(id: string) {
    setSelectedIds(previous => previous.includes(id) ? previous.filter(value => value !== id) : [...previous, id]);
  }
  function toggleAll() {
    const ids = filtered.map(item => item.id);
    setSelectedIds(previous => ids.every(id => previous.includes(id))
      ? previous.filter(id => !ids.includes(id)) : [...new Set([...previous, ...ids])]);
  }
  return { accounts, remote, mutation, items, filtered, accountId, changeAccount, selectedIds,
    searchTerm, setSearchTerm, filterMode, setFilterMode, toggle, toggleAll };
}
